import { Component } from "react";
import { ErrorPage } from "@components/error-boundary/error-page";
import { ErrorHandlingContext } from "@components/error-boundary/error-handling-context";
import { ErrorDialog } from "@components/error-boundary/error-dialog";
import { ErrorToast } from "@components/error-boundary/error-toast";
import {
  HandleableError,
  ErrorWithTitle,
  UiErrorType,
} from "@components/error-boundary/error-types";
import { ReactChildrenOnly } from "@utils/utility-types";
import { sentryCaptureError } from "@utils/sentry-utils";
import { shouldSkipSentryForError } from "@components/error-boundary/error-boundary-utils";

type State = {
  [key in UiErrorType]: ErrorWithTitle | null;
};

/**
 * Use to create a new ErrorBoundary and ErrorHandlingContext.
 * Needs to be a class component due to componentDidCatch not being available in function components.
 */
// eslint-disable-next-line react/prefer-es6-class
export class ErrorBoundary extends Component<ReactChildrenOnly, State> {
  constructor(props: ReactChildrenOnly) {
    super(props);
    this.state = { page: null, dialog: null, toast: null };
  }

  /**
   * Catches all React-rendering errors within the children.
   * * This life-cycle catch any unintentional/unexpected error that is not covered by use-error-handler
   */
  componentDidCatch(error: Error): void {
    this.handleErrorWithPage({
      id: `componentDidCatch-${Date.now().toString()}`,
      title: "An unexpected error occurred: ",
      error,
    });
  }

  /** Log error in Sentry */
  logError(title: string, error: HandleableError): void {
    if (!shouldSkipSentryForError(error)) {
      sentryCaptureError({ error, title });
    }
  }

  /** Handle the error by showing a page */
  handleErrorWithPage = ({ id, title, error }: ErrorWithTitle): void => {
    this.logError(title, error);
    this.setState({
      page: {
        id,
        title,
        error,
      },
    });
  };

  /** Handle the error by showing a dialog */
  handleErrorWithDialog = ({ id, title, error }: ErrorWithTitle): void => {
    this.logError(title, error);
    this.setState({
      dialog: {
        id,
        title,
        error,
      },
    });
  };

  /** Handle the error by showing a toast */
  handleErrorWithToast = ({ id, title, error }: ErrorWithTitle): void => {
    this.logError(title, error);
    this.setState({
      toast: {
        id,
        title,
        error,
      },
    });
  };

  handleClosePageError = (): void => {
    this.setState({ page: null });
  };

  handleCloseDialogError = (): void => {
    this.setState({ dialog: null });
  };

  handleCloseToastError = (): void => {
    this.setState({ toast: null });
  };

  handleOnGoHome = (): void => {
    this.handleClosePageError();

    // React-router is not available in root ErrorBoundary
    globalThis.location.href = "/";
  };

  render(): React.JSX.Element {
    if (this.state.page) {
      return (
        <ErrorPage
          error={this.state.page}
          onReload={this.handleClosePageError}
          onGoHome={this.handleOnGoHome}
        />
      );
    }

    return (
      <ErrorHandlingContext.Provider
        value={{
          handleErrorWithDialog: this.handleErrorWithDialog,
          handleErrorWithToast: this.handleErrorWithToast,
          handleErrorWithPage: this.handleErrorWithPage,
        }}
      >
        {this.props.children}
        {this.state.dialog && (
          <ErrorDialog
            error={this.state.dialog}
            onClose={this.handleCloseDialogError}
          />
        )}
        {this.state.toast && (
          <ErrorToast
            error={this.state.toast}
            onClose={this.handleCloseToastError}
          />
        )}
      </ErrorHandlingContext.Provider>
    );
  }
}
